"use client";

import React, { useCallback } from "react";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { JsonFieldDisplay } from "../JsonFieldDisplay/JsonFieldDisplay";
import { JsonFieldEditor } from "../JsonFieldEditor/JsonFieldEditor";
import type { JsonFieldValue, JsonValue } from "@/components/shared/types";

interface JsonFieldCellProps {
  value: JsonFieldValue;
  fieldId: string;
  entityId: string;
  entityName: string;
  onSave: (newValue: Record<string, JsonValue>) => Promise<void>;
  maxDisplayItems?: number;
}

/**
 * Unified JSON field cell component for both table and modal contexts
 * Handles display with badges and inline editing via modal
 * Eliminates duplication between table and detail views
 */
export function JsonFieldCell({
  value,
  fieldId,
  entityId,
  entityName,
  onSave,
  maxDisplayItems = 2,
}: JsonFieldCellProps) {
  const globalModalId = `json-editor-${entityName}-${entityId}-${fieldId}`;

  const handleSave = useCallback(
    async (newValue: Record<string, JsonValue>) => {
      try {
        await onSave(newValue);
      } catch (error) {
        console.error("Error saving JSON field:", error);
        throw error;
      }
    },
    [onSave]
  );

  return (
    <div className="min-w-0 flex items-center gap-2">
      <TooltipWithContent content="Click to view and edit details">
        <div className="flex-1 cursor-pointer hover:opacity-80 transition-opacity">
          <JsonFieldDisplay value={value} fieldId={fieldId} maxItems={maxDisplayItems} />
        </div>
      </TooltipWithContent>
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <JsonFieldEditor
          value={value}
          fieldId={fieldId}
          onSave={handleSave}
          globalModalId={globalModalId}
        />
      </div>
    </div>
  );
}
